/*
 * Koyozo store voucher box (snippets/koyozo-store-voucher.liquid).
 *
 * A custom element, because the cart drawer redraws its markup without
 * running inline scripts: defined once per page here, every redrawn
 * <kz-store-voucher> wires itself up when it is connected.
 *
 * Flow: the code is checked first (nothing is spent). If the voucher is worth
 * more than the gift cards in the cart, the customer is warned and must
 * confirm; otherwise it is used straight away. The backend then returns a
 * single-use code, which is added to the cart (keeping any codes already
 * there) and the customer lands on the cart page with it applied.
 */
(function () {
  if (!window.customElements || customElements.get('kz-store-voucher')) return;

  var GENERIC = 'Something went wrong. Please try again in a minute.';

  function rupees(n) { return '₹' + Number(n).toLocaleString('en-IN', { maximumFractionDigits: 2 }); }

  function getCart() {
    return fetch('/cart.js', { headers: { Accept: 'application/json' } }).then(function (r) { return r.json(); });
  }

  // Gift cards in the cart right now, in rupees.
  function giftTotal(cart) {
    return (cart.items || []).reduce(function (s, it) {
      return s + (String(it.sku || '').indexOf('EXLR8:') === 0 ? Number(it.final_line_price) || 0 : 0);
    }, 0) / 100;
  }

  class KzStoreVoucher extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      var form = this.querySelector('[data-kz-form]');
      if (!form) return; // the "voucher applied" state has nothing to wire
      this._ready = true;
      var self = this;
      this.input = this.querySelector('[data-kz-code]');
      this.btn = this.querySelector('[data-kz-btn]');
      this.msg = this.querySelector('[data-kz-msg]');
      this.box = this.querySelector('[data-kz-confirm]');
      this.boxText = this.querySelector('[data-kz-confirm-text]');
      this.yes = this.querySelector('[data-kz-yes]');
      this.label = this.btn.textContent;
      this.pending = null;
      form.addEventListener('submit', function (e) { e.preventDefault(); self.check(); });
      this.yes.addEventListener('click', function () { if (self.pending) self.use(self.pending); });
      this.querySelector('[data-kz-no]').addEventListener('click', function () {
        self.box.hidden = true; self.pending = null; self.say(''); self.input.focus();
      });
    }

    say(text, tone) { this.msg.textContent = text || ''; this.msg.setAttribute('data-tone', tone || ''); }

    busy(on, text) {
      this.btn.disabled = on; this.yes.disabled = on; this.input.readOnly = on;
      this.btn.textContent = on ? (text || 'Checking…') : this.label;
    }

    call(code, confirm) {
      return fetch(this.getAttribute('data-endpoint'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code: code, confirm: confirm })
      }).then(function (r) {
        return r.json().catch(function () { return null; }).then(function (b) { return { status: r.status, body: b || {} }; });
      });
    }

    failed(res) {
      var b = res.body || {};
      this.say((b.error && b.error[0]) || (res.status === 429 ? 'Too many attempts. Please wait a minute and try again.' : GENERIC), 'error');
      this.busy(false);
    }

    check() {
      var self = this;
      var code = this.input.value.trim();
      if (code.length < 6) { this.say('Please enter the full code from your voucher.', 'error'); this.input.focus(); return; }
      this.busy(true);
      this.say('');
      this.box.hidden = true;
      this.call(code, false).then(function (res) {
        var data = res.body.data || {};
        if (res.status !== 200) return self.failed(res);
        // Applied before and not used yet: the same code comes straight back.
        if (data.discountCode) return self.addToCart(data);
        if (!data.preview) return self.failed(res);
        return getCart().then(function (cart) {
          var total = giftTotal(cart);
          var worth = Number(data.amount);
          if (!(total > 0)) { self.say('Add a gift card to your cart first.', 'error'); self.busy(false); return; }
          if (worth <= total) return self.use(code);
          self.pending = code;
          self.boxText.textContent = 'This voucher is worth ' + rupees(worth) + ' and the gift cards in your cart come to ' +
            rupees(total) + '. The whole voucher is used in one go, so the other ' + rupees(worth - total) +
            " can't be kept. Add more gift cards to use it fully, or continue.";
          self.box.hidden = false;
          self.busy(false);
        });
      }).catch(function () { self.say(GENERIC, 'error'); self.busy(false); });
    }

    use(code) {
      var self = this;
      this.busy(true, 'Applying…');
      this.box.hidden = true;
      this.say('');
      this.call(code, true).then(function (res) {
        var data = res.body.data || {};
        if (res.status === 200 && data.discountCode) return self.addToCart(data);
        self.failed(res);
      }).catch(function () { self.say(GENERIC, 'error'); self.busy(false); });
    }

    // Add the code to the cart next to any codes already there, then show the
    // cart page with it applied. If the cart refuses it, the /discount/ link
    // still carries it to checkout.
    addToCart(data) {
      var self = this;
      this.say('Voucher accepted — updating your cart…');
      getCart().then(function (cart) {
        var codes = (cart.discount_codes || []).map(function (d) { return d.code; })
          .filter(function (c) { return c && c.toUpperCase() !== data.discountCode.toUpperCase(); });
        codes.push(data.discountCode);
        return fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ discount: codes.join(',') })
        }).then(function (r) { return r.json(); });
      }).then(function (cart) {
        var ok = (cart.discount_codes || []).some(function (d) {
          return d.code && d.code.toUpperCase() === data.discountCode.toUpperCase() && d.applicable !== false;
        });
        if (ok) { window.location.href = '/cart'; return; }
        window.location.href = data.applyUrl || '/cart';
      }).catch(function () { window.location.href = data.applyUrl || '/cart'; });
    }
  }

  customElements.define('kz-store-voucher', KzStoreVoucher);
})();
